import type { DeviceSettings } from "@tandemdiabetes/sugarmate-api-react-client"
import { ThemeAppearance } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  FieldSet,
  SelectButton,
  SelectButtons,
  Legend,
  InfoButton,
} from "@tandemdiabetes/sugarmate-react-components"
import type { ChangeEvent } from "react"
import { useCallback } from "react"

import styles from "./Theme.module.scss"
import {
  usePolyglot,
  useFollowedUsers,
} from "src/contexts"
import { useInputPropsForValue } from "src/hooks"

const polyglotPrefix = "pages.settings.display.theme_appearance."
const inputName = "theme-appearance"

interface Props {
  updatedAt?: string;
  updateDeviceSettings: (settings: Partial<DeviceSettings>) => void;
}

export function Appearance(props: Props) {
  const {
    updatedAt,
    updateDeviceSettings,
  } = props

  const polyglot = usePolyglot()
  const { currentDeviceSettings } = useFollowedUsers()
  const currentThemeAppearance = currentDeviceSettings.theme_appearance

  const adapter = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => e.target.value as ThemeAppearance,
    [],
  )

  const onValueChange = useCallback(
    (themeAppearance: DeviceSettings["theme_appearance"]) => {
      updateDeviceSettings({
        theme_appearance: themeAppearance,
      })
    },
    [updateDeviceSettings],
  )

  const {
    value,
    onChange,
  } = useInputPropsForValue({
    value: currentThemeAppearance,
    onChange: onValueChange,
    adapter,
    updatedAt,
  })

  return (
    <FieldSet>
      <Legend>
        {polyglot.t(`${polyglotPrefix}label`)}
        <InfoButton title={polyglot.t(`${polyglotPrefix}title`)}>
          {polyglot.t(`${polyglotPrefix}description`)}
        </InfoButton>
      </Legend>
      <SelectButtons className={styles.selectContainer}>
        <SelectButton
          htmlFor="theme-appearance-os"
          name={inputName}
          value={ThemeAppearance.OS}
          onChange={onChange}
          checked={value === ThemeAppearance.OS}
        >
          {polyglot.t(`${polyglotPrefix}os`)}
        </SelectButton>
        <SelectButton
          htmlFor="theme-appearance-light"
          name={inputName}
          value={ThemeAppearance.Light}
          onChange={onChange}
          checked={value === ThemeAppearance.Light}
        >
          {polyglot.t(`${polyglotPrefix}light`)}
        </SelectButton>
        <SelectButton
          htmlFor="theme-appearance-dark"
          name={inputName}
          value={ThemeAppearance.Dark}
          onChange={onChange}
          checked={value === ThemeAppearance.Dark}
        >
          {polyglot.t(`${polyglotPrefix}dark`)}
        </SelectButton>
      </SelectButtons>
    </FieldSet>
  )
}
